import { useState, useRef, useEffect, useCallback } from "react";
import { Microphone, MicrophoneSlash, Headset, Robot, UserCircle, Waveform } from "@phosphor-icons/react";
import { streamChat, fetchTTS, LANG_CODES } from "@/lib/api";
import { BrochureOrb } from "@/components/BrochureOrb";

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [56, 189, 248];
}

// Normalise a phrase for fuzzy comparison (lowercase, strip punctuation/space).
function normSpeech(s) {
  return (s || "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "").replace(/\s+/g, " ").trim();
}
// True when the recognised text is really the AI's own last reply leaking back
// into the mic — used to stop the agent looping on the same answer.
function isEchoOfReply(text, reply) {
  const a = normSpeech(text), b = normSpeech(reply);
  if (!a || !b) return false;
  if (a.length >= 6 && b.includes(a)) return true;
  const tokens = a.split(" ");
  const bset = new Set(b.split(" "));
  const common = tokens.filter((w) => bset.has(w)).length;
  return tokens.length >= 3 && common / tokens.length > 0.7;
}

/**
 * Browser-based voice console. STT via Web Speech API, TTS via ElevenLabs (with browser fallback).
 */
export default function VoicePanel({ chatPath, ttsPath, agent }) {
  const color = agent?.primary_color || "#0033FF";
  const langCode = LANG_CODES[agent?.language] || "en-US";
  const [supported, setSupported] = useState(true);
  const [listening, setListening] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | listening | thinking | speaking
  const [transcript, setTranscript] = useState([]);
  const [escalated, setEscalated] = useState(false);
  const [conversing, setConversing] = useState(false);
  const convRef = useRef(false);
  const recognitionRef = useRef(null);
  const sessionRef = useRef(`voice-${Math.random().toString(36).slice(2)}`);
  const logRef = useRef(null);
  const audioRef = useRef(null);
  const orbRgb = hexToRgb(color); // live brand colour — updates with the colour picker

  // User-selectable orb theme: "dark" (svart bakgrund) or "light" (vit bakgrund)
  const [orbTheme, setOrbTheme] = useState(() => localStorage.getItem("senzum_orb_theme") || "dark");
  useEffect(() => { localStorage.setItem("senzum_orb_theme", orbTheme); }, [orbTheme]);
  const dark = orbTheme === "dark";

  // Live audio amplitude (0..1) driven by the AI playback via Web Audio analyser
  const [level, setLevel] = useState(0);
  const [analysing, setAnalysing] = useState(false);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);

  // Full-duplex barge-in: persistent mic VAD lets the customer interrupt the AI mid-sentence.
  const bargeEnabled = agent?.barge_in !== false;
  const bargeEnabledRef = useRef(bargeEnabled);
  const statusRef = useRef("idle");
  const interruptedRef = useRef(false);
  const micStreamRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const [micLevel, setMicLevel] = useState(0);

  // Turn lock + echo guard: prevents two answers playing at once and stops the
  // AI from transcribing its OWN voice and looping on the same topic.
  const busyRef = useRef(false);        // a turn (think→speak) is in progress
  const lastReplyRef = useRef("");      // last thing the AI said (for echo rejection)
  const aiLevelRef = useRef(0);         // live AI playback amplitude (echo reference)
  const speakStartRef = useRef(0);      // when the current TTS playback began

  useEffect(() => { bargeEnabledRef.current = bargeEnabled; }, [bargeEnabled]);
  useEffect(() => { statusRef.current = status; }, [status]);

  const stopLevelLoop = useCallback(() => {
    if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
    levelRafRef.current = null;
    setLevel(0);
    aiLevelRef.current = 0;
    setAnalysing(false);
  }, []);

  const startLevelLoop = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    setAnalysing(true);
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length); // 0..~0.5
      const lvl = Math.min(1, rms * 3.2);
      aiLevelRef.current = lvl;
      setLevel(lvl);
      levelRafRef.current = requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const attachAnalyser = useCallback((audio) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = audioCtxRef.current || new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume();
      const srcNode = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      srcNode.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
      startLevelLoop();
    } catch {
      // analyser optional — playback still works
    }
  }, [startLevelLoop]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    const rec = new SR();
    rec.lang = langCode;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    window.speechSynthesis?.getVoices();
    return () => {
      convRef.current = false;
      try { rec.abort(); } catch {}
      window.speechSynthesis?.cancel();
      try { audioRef.current?.pause(); } catch {}
      if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
      if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
      try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      try { audioCtxRef.current?.close(); } catch {}
    };
  }, [langCode]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [transcript, status]);

  const stopAudio = () => {
    window.speechSynthesis?.cancel();
    try { audioRef.current?.pause(); audioRef.current = null; } catch {}
    stopLevelLoop();
  };

  const cleanForSpeech = (text) =>
    (text || "")
      .replace(/\[\[[^\]]*\]\]/g, "")
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2300}-\u{23FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}]/gu, "")
      .replace(/[*_`#>|]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const beginListenRef = useRef(() => {});

  // Trigger a barge-in: silence the AI immediately and start listening to the customer.
  const bargeIn = useCallback(() => {
    if (statusRef.current !== "speaking") return;
    interruptedRef.current = true;
    busyRef.current = false; // current turn is abandoned — release the lock
    stopAudio();
    setStatus("listening");
    if (convRef.current) beginListenRef.current();
  }, []);

  // Persistent microphone voice-activity detection (with echo cancellation to avoid self-trigger).
  const setupMic = useCallback(async () => {
    if (micStreamRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      micStreamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = audioCtxRef.current || new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser); // intentionally NOT connected to destination (no feedback)
      micAnalyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      let voiceFrames = 0;
      const VAD_THRESHOLD = 0.17; // RMS above which we consider it real speech
      const VAD_SUSTAIN = 6; // ~frames of sustained speech before barging in
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const micL = Math.min(1, rms * 3.2);
        setMicLevel(micL);
        if (statusRef.current === "speaking" && bargeEnabledRef.current) {
          // Only treat the mic as a real interruption when it is CLEARLY louder
          // than the AI's own playback (echo) and after a short grace period —
          // this stops the agent from interrupting/looping on its own voice.
          const since = Date.now() - speakStartRef.current;
          const exceedsEcho = micL > aiLevelRef.current * 1.7 + 0.14;
          if (rms > VAD_THRESHOLD && exceedsEcho && since > 600) {
            voiceFrames += 1;
            if (voiceFrames >= VAD_SUSTAIN) { voiceFrames = 0; bargeIn(); }
          } else {
            voiceFrames = Math.max(0, voiceFrames - 1);
          }
        } else {
          voiceFrames = 0;
        }
        vadRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // mic VAD optional — fall back to half-duplex turn-taking
    }
  }, [bargeIn]);

  const teardownMic = useCallback(() => {
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    vadRafRef.current = null;
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    micStreamRef.current = null;
    micAnalyserRef.current = null;
    setMicLevel(0);
  }, []);

  const browserSpeak = (text, onComplete) => {
    if (!window.speechSynthesis) { onComplete?.(); return; }
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(cleanForSpeech(text));
    utter.lang = langCode;
    utter.rate = agent?.speaking_rate ?? 1;
    const voices = window.speechSynthesis.getVoices() || [];
    const v = voices.find((x) => x.lang?.toLowerCase().startsWith(langCode.slice(0, 2).toLowerCase()));
    if (v) utter.voice = v;
    utter.onstart = () => { setStatus("speaking"); speakStartRef.current = Date.now(); };
    utter.onend = () => onComplete?.();
    utter.onerror = () => onComplete?.();
    window.speechSynthesis.speak(utter);
  };

  const speak = async (text, onComplete) => {
    const clean = (text || "").trim();
    if (!clean) { onComplete?.(); return; }
    stopAudio();                    // guarantee nothing else is still playing (no overlap)
    interruptedRef.current = false;
    lastReplyRef.current = clean;   // remember what the AI says for echo rejection
    // Prefer natural ElevenLabs voice when configured
    if (ttsPath && agent?.voice) {
      try {
        setStatus("speaking");
        const url = await fetchTTS({ path: ttsPath, text: clean });
        const audio = new Audio(url);
        audio.playbackRate = Math.min(1.5, Math.max(0.6, agent?.speaking_rate ?? 1));
        audioRef.current = audio;
        const finish = () => { stopLevelLoop(); URL.revokeObjectURL(url); onComplete?.(); };
        audio.onended = finish;
        audio.onerror = finish;
        attachAnalyser(audio);
        await audio.play();
        speakStartRef.current = Date.now();
        return;
      } catch (e) {
        // fall back to browser synth below
      }
    }
    browserSpeak(clean, onComplete);
  };

  const handleUserText = useCallback(async (text) => {
    if (busyRef.current) return;   // a turn is already in flight — never start a second
    busyRef.current = true;
    setTranscript((t) => [...t, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStatus("thinking");
    let full = "";
    await streamChat({
      path: chatPath,
      message: text,
      sessionId: sessionRef.current,
      channel: "voice",
      onDelta: (d) => {
        full += d;
        setTranscript((t) => {
          const copy = [...t];
          copy[copy.length - 1] = { role: "assistant", content: full };
          return copy;
        });
      },
      onDone: (p) => {
        if (p.escalated) setEscalated(true);
        // Speak the clean server reply (never the raw stream, which may contain hand-off tokens)
        const reply = (p.reply ?? full).trim() || "Förlåt, kan du upprepa det?";
        setTranscript((t) => {
          const copy = [...t];
          copy[copy.length - 1] = { role: "assistant", content: reply };
          return copy;
        });
        speak(reply, () => {
          busyRef.current = false;   // turn fully complete — safe to listen again
          if (interruptedRef.current) { interruptedRef.current = false; return; }
          if (convRef.current) beginListenRef.current();
          else setStatus("idle");
        });
      },
      onError: () => {
        busyRef.current = false;
        setStatus("idle");
        setTranscript((t) => {
          const copy = [...t];
          copy[copy.length - 1] = { role: "assistant", content: "Anslutningsfel." };
          return copy;
        });
        if (convRef.current) setTimeout(() => convRef.current && beginListenRef.current(), 600);
      },
    });
  }, [chatPath]);

  // Hands-free turn-taking loop: listen → on result process → after reply, listen again
  const beginListen = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec || !convRef.current) return;
    let gotResult = false;
    setListening(true);
    setStatus("listening");
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setListening(false);
      gotResult = true;
      // Drop the AI's own voice leaking into the mic, or input arriving while a
      // turn is still being processed — this is what caused the looping + overlap.
      if (busyRef.current || isEchoOfReply(text, lastReplyRef.current)) {
        if (convRef.current) setTimeout(() => convRef.current && beginListenRef.current(), 200);
        return;
      }
      handleUserText(text);
    };
    rec.onerror = (e) => {
      setListening(false);
      const err = e?.error;
      // Mic blocked / unavailable on this device → end the conversation gracefully
      if (err === "not-allowed" || err === "service-not-allowed" || err === "audio-capture") {
        convRef.current = false;
        setConversing(false);
        setStatus("idle");
      }
    };
    rec.onend = () => {
      setListening(false);
      // No speech captured yet and conversation still active → keep listening (hands-free)
      if (convRef.current && !gotResult) {
        setTimeout(() => { if (convRef.current) beginListen(); }, 250);
      }
    };
    try { rec.start(); } catch {}
  }, [handleUserText]);

  useEffect(() => { beginListenRef.current = beginListen; }, [beginListen]);

  const startConversation = () => {
    if (!recognitionRef.current) return;
    // Pre-warm AudioContext on the user gesture so AI playback is audible
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
        if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
      }
    } catch {}
    convRef.current = true;
    busyRef.current = false;
    interruptedRef.current = false;
    setConversing(true);
    setupMic();
    beginListen();
  };

  const stopConversation = () => {
    convRef.current = false;
    busyRef.current = false;
    setConversing(false);
    try { recognitionRef.current?.abort(); } catch {}
    stopAudio();
    teardownMic();
    setListening(false);
    setStatus("idle");
  };

  const toggleConversation = () => (conversing ? stopConversation() : startConversation());


  const statusLabel = conversing
    ? {
        idle: "Lyssnar…",
        listening: "Lyssnar… prata på",
        thinking: "Tänker…",
        speaking: bargeEnabled ? "Talar… (du kan avbryta när som helst)" : "Talar…",
      }[status]
    : "Tryck på sfären för att starta samtalet";

  if (!supported) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-white text-zinc-900">
        <MicrophoneSlash size={40} className="text-zinc-400 mb-4" />
        <p className="text-zinc-600 max-w-xs">Webbläsarröst stöds inte här. Använd Google Chrome för att testa röstagenter.</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full relative overflow-hidden transition-colors duration-300 ${dark ? "bg-black text-zinc-100" : "bg-white text-zinc-900"}`}>
      <div className="relative flex-1 flex flex-col items-center justify-center p-6">
        <button
          data-testid="voice-mic-button"
          onClick={toggleConversation}
          aria-label={conversing ? "Avsluta samtalet" : "Starta samtalet"}
          className="relative rounded-full transition-transform hover:scale-[1.03] active:scale-95 focus:outline-none"
        >
          <div className="relative flex items-center justify-center" style={{ width: 320, height: 320 }}>
            <BrochureOrb
              speaking={status === "speaking" || status === "listening"}
              level={status === "speaking" && analysing ? level : (status === "listening" && conversing ? micLevel : null)}
              size={320}
              color={orbRgb}
              theme={orbTheme}
              className="relative z-10"
            />
            <span className={`absolute inset-x-0 bottom-7 z-10 flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider pointer-events-none ${dark ? "text-white/80" : "text-zinc-500"}`}>
              {!conversing
                ? <><Microphone size={13} weight="fill" /> Starta samtal</>
                : status === "speaking"
                  ? <><Waveform size={13} weight="fill" /> Talar</>
                  : status === "thinking"
                    ? "Tänker…"
                    : <><MicrophoneSlash size={13} weight="fill" /> Avsluta</>}
            </span>
          </div>
        </button>

        <p data-testid="voice-status" className={`mt-6 text-sm font-medium ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{statusLabel}</p>

        <div className="mt-5 flex items-center gap-1" data-testid="orb-bg-toggle">
          <div className={`inline-flex items-center rounded-full p-1 ${dark ? "bg-white/10" : "bg-zinc-100"}`}>
            <button
              type="button"
              data-testid="orb-bg-dark"
              onClick={() => setOrbTheme("dark")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${dark ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}
            >
              Svart
            </button>
            <button
              type="button"
              data-testid="orb-bg-light"
              onClick={() => setOrbTheme("light")}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${!dark ? "bg-[#0033FF] text-white shadow-sm" : "text-zinc-400 hover:text-zinc-200"}`}
            >
              Vit
            </button>
          </div>
        </div>
      </div>

      {/* transcript */}
      <div ref={logRef} className={`relative max-h-48 overflow-y-auto scroll-thin border-t p-4 space-y-2 ${dark ? "border-white/10" : "border-zinc-200"}`}>
        {transcript.length === 0 && (
          <p className={`text-center text-xs ${dark ? "text-zinc-500" : "text-zinc-400"}`}>Din liveutskrift visas här.</p>
        )}
        {transcript.map((m, i) => (
          <div key={i} className="flex gap-2 text-sm">
            <span className="shrink-0 mt-0.5">
              {m.role === "user"
                ? <UserCircle size={16} weight="fill" className="text-zinc-400" />
                : <Robot size={16} weight="fill" style={{ color }} />}
            </span>
            <span className={m.role === "user" ? (dark ? "text-zinc-400" : "text-zinc-600") : (dark ? "text-zinc-100" : "text-zinc-900")}>{m.content}</span>
          </div>
        ))}
        {escalated && (
          <div data-testid="voice-escalation-banner" className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm">
            <Headset size={18} weight="bold" /> Kopplar samtalet till en mänsklig agent…
          </div>
        )}
      </div>
    </div>
  );
}
